import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AbasSublinhadas, DetailHeader, type Aba } from "@/components/padroes";
import { FolhaResumo } from "@/components/folha/FolhaResumo";
import { FolhaItensTable } from "@/components/folha/FolhaItensTable";
import { PendenciasImportacao } from "@/components/folha/PendenciasImportacao";
import { FecharFolhaDialog } from "@/components/folha/FecharFolhaDialog";
import { StatusFolha } from "@/components/folha/StatusFolha";
import { AvisoContextual } from "@/components/folha/AvisoContextual";
import { avisarResultadoErp } from "@/components/erp/avisarResultadoErp";
import { AjustarItemModal } from "@/components/folha/AjustarItemModal";
import { DemonstrativoCard } from "@/components/folha/DemonstrativoCard";
import { ComparativoAnterior } from "@/components/folha/ComparativoAnterior";
import { StatusEmissaoPainel } from "@/components/folha/StatusEmissaoPainel";
import { UploadPlanilhaModal } from "@/components/UploadPlanilhaModal";
import { HoleritesCltPainel } from "@/components/folha/HoleritesCltPainel";
import { ImportarExtratoCltModal } from "@/components/folha/ImportarExtratoCltModal";
import { useAuth } from "@/context/AuthContext";
import { atualizarContadoresSidebar } from "@/hooks/useContadoresSidebar";
import { api } from "@/lib/api";
import { triggerDownload } from "@/lib/download";
import { formatCents, formatDateTime, formatMesRef } from "@/lib/format";
import {
  ROTULO_TIPO_PLANILHA,
  type CodigoBloqueio,
  type FolhaDetalhe as Detalhe,
  type FolhaItem,
  type HoleriteResumo,
  type Pendencia,
  type PendenciaIgnorada,
  type StatusEmissao,
  type VariacaoItem,
} from "@/data/folha";
import type { User } from "@/data/mock";
import { pendenciasErp, type LancamentosErp, type ResultadoEnvioErp } from "@/data/erp";
import { ErpLancamentosPainel } from "@/components/erp/ErpLancamentosPainel";
import { toast } from "sonner";
import {
  BriefcaseBusiness,
  ChevronDown,
  Download,
  FileSpreadsheet,
  HeartPulse,
  Landmark,
  Loader2,
  Lock,
  LockOpen,
  RefreshCw,
  Search,
  Send,
  Smile,
  TrendingUp,
  Upload,
  UserX,
} from "lucide-react";

interface Afetado {
  colaboradorId: string;
  nome: string;
  nfNumero: string;
  nfStatus: string;
}

type AbaFolha = "itens" | "clt" | "pendencias" | "comparativo" | "emissao" | "erp" | "planilhas";

const RHFolhaDetalhe = () => {
  const { id = "" } = useParams();
  const { user } = useAuth();

  const [detalhe, setDetalhe] = useState<Detalhe | null>(null);
  const [pendencias, setPendencias] = useState<Pendencia[]>([]);
  const [ignoradas, setIgnoradas] = useState<PendenciaIgnorada[]>([]);
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [variacoes, setVariacoes] = useState<VariacaoItem[]>([]);
  const [emissao, setEmissao] = useState<StatusEmissao | null>(null);
  const [holerites, setHolerites] = useState<HoleriteResumo[]>([]);
  const [erp, setErp] = useState<LancamentosErp | null>(null);
  const [enviandoErp, setEnviandoErp] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");
  const [aba, setAba] = useState<AbaFolha>("itens");

  const [uploadComissoes, setUploadComissoes] = useState(false);
  const [uploadSaude, setUploadSaude] = useState(false);
  const [uploadOdonto, setUploadOdonto] = useState(false);
  const [uploadExtrato, setUploadExtrato] = useState(false);
  const [fecharOpen, setFecharOpen] = useState(false);
  const [ajustando, setAjustando] = useState<FolhaItem | null>(null);
  const [detalhando, setDetalhando] = useState<FolhaItem | null>(null);
  const [recalculando, setRecalculando] = useState(false);

  const [reabrirOpen, setReabrirOpen] = useState(false);
  const [motivoReabertura, setMotivoReabertura] = useState("");
  const [afetados, setAfetados] = useState<Afetado[]>([]);
  const [reabrindo, setReabrindo] = useState(false);

  const ehRH = user?.role === "rh";
  const rascunho = detalhe?.folha.status === "rascunho";
  const editavel = ehRH && rascunho;

  const load = useCallback(async () => {
    try {
      const query = busca.trim() ? `?q=${encodeURIComponent(busca.trim())}` : "";
      const d = await api.get<Detalhe>(`/folhas/${id}${query}`);
      setDetalhe(d);

      const [comp, st, hs, lancamentosErp] = await Promise.all([
        api.get<VariacaoItem[]>(`/folhas/${id}/comparativo`),
        api.get<StatusEmissao>(`/folhas/${id}/status-emissao`),
        api.get<HoleriteResumo[]>(`/folhas/${id}/holerites`),
        api.get<LancamentosErp>(`/folhas/${id}/erp`),
      ]);
      setVariacoes(comp);
      setEmissao(st);
      setHolerites(hs);
      setErp(lancamentosErp);

      if (user?.role === "rh") {
        const [p, ign, u] = await Promise.all([
          api.get<Pendencia[]>(`/folhas/${id}/pendencias`),
          api.get<PendenciaIgnorada[]>(`/folhas/${id}/pendencias/ignoradas`),
          api.get<User[]>("/colaboradores"),
        ]);
        setPendencias(p);
        setIgnoradas(ign);
        setUsuarios(u);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar a folha");
    } finally {
      setCarregando(false);
    }
  }, [id, busca, user?.role]);

  useEffect(() => {
    void load();
  }, [load]);

  // Importar, recalcular, resolver pendência, fechar/reabrir: os contadores da
  // sidebar (holerites divergentes + linhas sem colaborador) mudam junto.
  const aposMudanca = useCallback(async () => {
    await load();
    atualizarContadoresSidebar();
  }, [load]);

  const recalcular = async () => {
    setRecalculando(true);
    try {
      await api.post(`/folhas/${id}/recalcular`, {});
      toast.success("Folha recalculada");
      await aposMudanca();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao recalcular");
    } finally {
      setRecalculando(false);
    }
  };

  /** PJ e CLT juntos. "completo": colunas da folha PJ + as da aba CLT; "financeiro": o que o financeiro paga. */
  const exportar = async (tipo: "completo" | "financeiro") => {
    try {
      const { blob, filename } = await api.getFile(`/folhas/${id}/export.csv?tipo=${tipo}`);
      triggerDownload(
        blob,
        filename ||
          `Folha - ${detalhe?.folha.competencia}${tipo === "financeiro" ? " - Financeiro" : ""}.csv`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao exportar");
    }
  };

  /** "Corrigir agora" no diálogo de fechamento: leva para onde o bloqueio se resolve. */
  const corrigirBloqueio = (codigo: CodigoBloqueio) => {
    switch (codigo) {
      case "PENDENCIAS_IMPORTACAO":
        setAba("pendencias");
        break;
      case "SEM_COPARTICIPACAO":
        setUploadSaude(true);
        break;
      case "HOLERITE_DIVERGENTE":
        setAba("clt");
        break;
      case "LIQUIDO_NAO_POSITIVO":
      case "ITEM_SEM_TOMADOR":
        setBusca("");
        setAba("itens");
        break;
    }
  };

  const reenviarErp = async () => {
    setEnviandoErp(true);
    try {
      const r = await api.post<ResultadoEnvioErp>(`/folhas/${id}/erp/enviar`, {});
      avisarResultadoErp(r);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao lançar no ERP");
    } finally {
      setEnviandoErp(false);
    }
  };

  const abrirReabertura = async () => {
    try {
      setAfetados(await api.get<Afetado[]>(`/folhas/${id}/impacto-reabertura`));
      setMotivoReabertura("");
      setReabrirOpen(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao verificar impacto");
    }
  };

  const reabrir = async () => {
    setReabrindo(true);
    try {
      await api.post(`/folhas/${id}/reabrir`, { motivo: motivoReabertura.trim() });
      toast.success("Folha reaberta — o aviso de emissão foi suspenso");
      setReabrirOpen(false);
      await aposMudanca();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao reabrir");
    } finally {
      setReabrindo(false);
    }
  };

  const removerLote = async (loteId: string) => {
    try {
      await api.delete(`/folhas/${id}/lotes/${loteId}`);
      toast.success("Importação removida");
      await aposMudanca();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao remover");
    }
  };

  if (carregando) {
    return (
      <RoleGuard role={["rh", "financeiro"]}>
        <AppLayout title="Folha de Pagamento">
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </AppLayout>
      </RoleGuard>
    );
  }

  if (!detalhe) {
    return (
      <RoleGuard role={["rh", "financeiro"]}>
        <AppLayout title="Folha de Pagamento">
          <p className="py-20 text-center text-muted-foreground">Competência não encontrada.</p>
        </AppLayout>
      </RoleGuard>
    );
  }

  const { folha, itens, totais } = detalhe;
  const fechada = folha.status === "fechada";
  const qtdPendenciasErp = pendenciasErp(erp?.lancamentos);
  const avisos = detalhe.avisos ?? [];
  const jaEnviadoErp = (erp?.lancamentos.length ?? 0) > 0;

  const abas: Aba<AbaFolha>[] = [
    { value: "itens", label: "PJ", contador: itens.length },
    { value: "clt", label: "CLT", contador: holerites.length, alerta: holerites.some((h) => h.divergente) },
    ...(ehRH
      ? [
          {
            value: "pendencias" as const,
            label: "Pendências",
            contador: detalhe.pendencias > 0 ? detalhe.pendencias : null,
            alerta: true,
          },
        ]
      : []),
    { value: "comparativo", label: "Comparativo" },
    ...(fechada
      ? [{ value: "emissao" as const, label: "Emissão", contador: emissao && emissao.pendentes > 0 ? emissao.pendentes : null }]
      : []),
    ...(fechada || (erp?.lancamentos.length ?? 0) > 0
      ? [{ value: "erp" as const, label: "ERP", contador: qtdPendenciasErp > 0 ? qtdPendenciasErp : null, alerta: true }]
      : []),
    { value: "planilhas", label: "Planilhas", contador: folha.lotes.length },
  ];
  // Reabrir tira as abas Emissão/ERP: volta para a primeira se a atual sumiu.
  const abaAtiva: AbaFolha = abas.some((a) => a.value === aba) ? aba : "itens";

  const importacoes = [
    { rotulo: "Comissões", icon: TrendingUp, abrir: () => setUploadComissoes(true) },
    { rotulo: "Amil Saúde", icon: HeartPulse, abrir: () => setUploadSaude(true) },
    { rotulo: "Amil Odonto", icon: Smile, abrir: () => setUploadOdonto(true) },
    { rotulo: "Extrato CLT (contabilidade)", icon: BriefcaseBusiness, abrir: () => setUploadExtrato(true) },
  ];

  return (
    <RoleGuard role={["rh", "financeiro"]}>
      <AppLayout title={`Folha — ${formatMesRef(folha.competencia)}`}>
        <div className="mx-auto flex max-w-7xl flex-col gap-4 tabular-nums">
          <DetailHeader
            voltar={{ to: "/rh/folha", label: "Competências" }}
            titulo={<span className="capitalize">{formatMesRef(folha.competencia)}</span>}
            status={<StatusFolha status={folha.status} comPendencia={avisos.length > 0} />}
            subtitulo={
              (folha.fechadaEm || folha.reabertaCount > 0) && (
                <>
                  {folha.fechadaEm && <>Fechada em {formatDateTime(folha.fechadaEm)}</>}
                  {folha.fechadaEm && folha.reabertaCount > 0 && " · "}
                  {folha.reabertaCount > 0 && <>reaberta {folha.reabertaCount}x</>}
                </>
              )
            }
            acoes={
              <>
                {editavel && (
                  <>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Upload />
                          Importar planilha
                          <ChevronDown className="text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="min-w-[230px] rounded-[10px] shadow-dropdown">
                        {importacoes.map(({ rotulo, icon: Icon, abrir }) => (
                          <DropdownMenuItem key={rotulo} onSelect={abrir} className="gap-2.5 py-2 text-[13px]">
                            <Icon className="h-[15px] w-[15px] text-muted-foreground" />
                            {rotulo}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void recalcular()}
                      disabled={recalculando}
                    >
                      {recalculando ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                      Recalcular
                    </Button>
                  </>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Download />
                      Exportar CSV
                      <ChevronDown className="text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[210px] rounded-[10px] shadow-dropdown">
                    <DropdownMenuItem onSelect={() => void exportar("completo")} className="gap-2.5 py-2 text-[13px]">
                      <FileSpreadsheet className="h-[15px] w-[15px] text-muted-foreground" />
                      Relatório Completo
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => void exportar("financeiro")} className="gap-2.5 py-2 text-[13px]">
                      <Landmark className="h-[15px] w-[15px] text-muted-foreground" />
                      Relatório Financeiro
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {editavel && (
                  <Button size="sm" onClick={() => setFecharOpen(true)}>
                    <Lock />
                    Fechar competência
                  </Button>
                )}

                {ehRH && fechada && (
                  <Button variant="outline" size="sm" onClick={() => void abrirReabertura()}>
                    <LockOpen />
                    Reabrir
                  </Button>
                )}

                {/* Manual de propósito: a folha costuma ser reaberta para as
                    comissões, e o ERP não atualiza conta já lançada. */}
                {ehRH && fechada && (
                  <Button size="sm" onClick={() => void reenviarErp()} disabled={enviandoErp}>
                    {enviandoErp ? <Loader2 className="animate-spin" /> : <Send />}
                    Enviar para o ERP
                  </Button>
                )}
              </>
            }
          />

          {fechada && avisos.length > 0 && (
            <AvisoContextual tom="alerta" icon={UserX}>
              <strong className="font-semibold">
                Fechada com pendência: {avisos.length} colaborador(es) com cadastro incompleto.
              </strong>{" "}
              Eles só conseguem enviar a NF depois de completar o cadastro, e ficam de fora do envio
              ao ERP até lá. Quando completarem, a pendência some sozinha — clique em{" "}
              <strong className="font-semibold">Enviar para o ERP</strong> de novo para lançá-los.
              <ul className="mt-1.5 list-disc pl-5 text-xs">
                {avisos.map((a) => (
                  <li key={a.id}>
                    {a.nome} — {a.detalhe}
                  </li>
                ))}
              </ul>
            </AvisoContextual>
          )}

          <FolhaResumo totais={totais} colaboradores={itens.length} />

          <div className="flex flex-col gap-4">
            <AbasSublinhadas value={abaAtiva} onChange={setAba} abas={abas} />

            {abaAtiva === "itens" && (
              <div className="space-y-3">
                <label className="relative flex max-w-sm items-center">
                  <Search className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar por nome ou e-mail"
                    className="pl-9"
                  />
                </label>
                <FolhaItensTable
                  itens={itens}
                  editavel={editavel}
                  onAjustar={setAjustando}
                  onDetalhar={setDetalhando}
                />
              </div>
            )}

            {abaAtiva === "clt" && (
              <HoleritesCltPainel
                folhaId={id}
                holerites={holerites}
                editavel={editavel}
                colaboradores={usuarios}
                onChange={() => void aposMudanca()}
              />
            )}

            {abaAtiva === "pendencias" && ehRH && (
              <PendenciasImportacao
                folhaId={id}
                pendencias={pendencias}
                ignoradas={ignoradas}
                bloqueiosIgnorados={folha.bloqueiosIgnorados ?? []}
                editavel={editavel}
                usuarios={usuarios}
                onResolvida={() => void aposMudanca()}
              />
            )}

            {abaAtiva === "comparativo" && <ComparativoAnterior variacoes={variacoes} />}

            {abaAtiva === "emissao" && emissao && (
              <StatusEmissaoPainel
                folhaId={id}
                status={emissao}
                podeCobrar={ehRH}
                onAtualizar={load}
              />
            )}

            {abaAtiva === "erp" && (
              <ErpLancamentosPainel
                titulo="Contas a pagar no ERP"
                dados={erp}
                rotuloNome="Colaborador"
                mostrarTipo
                textoVazio="Nada lançado ainda. Depois de fechar a competência, clique em Enviar para o ERP."
                onEnviar={ehRH && fechada ? () => void reenviarErp() : undefined}
                rotuloEnviar={jaEnviadoErp ? "Reenviar ao ERP" : "Enviar para o ERP"}
                enviando={enviandoErp}
              />
            )}

            {abaAtiva === "planilhas" && (
              <div className="overflow-hidden rounded-xl border bg-card">
                <Table className="min-w-[860px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-5">Arquivo</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Linhas</TableHead>
                      <TableHead className="text-right">Associadas</TableHead>
                      <TableHead className="text-right">Pendentes</TableHead>
                      <TableHead>Importado em</TableHead>
                      {editavel && <TableHead className="w-24" />}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {folha.lotes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={editavel ? 7 : 6} className="py-12 text-center text-muted-foreground">
                          Nenhuma planilha importada nesta competência.
                        </TableCell>
                      </TableRow>
                    ) : (
                      folha.lotes.map((l) => (
                        <TableRow key={l.id}>
                          <TableCell className="max-w-[320px] truncate pl-5 font-medium">{l.arquivoNome}</TableCell>
                          <TableCell>{ROTULO_TIPO_PLANILHA[l.tipo] ?? l.tipo}</TableCell>
                          <TableCell className="text-right">{l.totalLinhas}</TableCell>
                          <TableCell className="text-right">{l.casadas}</TableCell>
                          <TableCell className={`text-right ${l.naoCasadas > 0 ? "font-semibold text-destructive" : "text-muted-foreground"}`}>
                            {l.naoCasadas}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{formatDateTime(l.createdAt)}</TableCell>
                          {editavel && (
                            <TableCell className="pr-4 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-[13px] text-destructive hover:text-destructive"
                                onClick={() => void removerLote(l.id)}
                              >
                                Remover
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>
      </AppLayout>

      <ImportarExtratoCltModal
        open={uploadExtrato}
        onClose={() => setUploadExtrato(false)}
        folhaId={id}
        onSuccess={aposMudanca}
      />

      <UploadPlanilhaModal
        open={uploadComissoes}
        onClose={() => setUploadComissoes(false)}
        endpoint={`/folhas/${id}/importar/comissoes`}
        titulo="Importar planilha de comissões"
        descricao={
          <>
            Colunas aceitas: <strong>E-mail</strong>, <strong>CPF</strong>, <strong>CNPJ</strong>,{" "}
            <strong>Nome</strong>, <strong>Valor</strong> (obrigatório) e{" "}
            <strong>Descrição</strong>. O colaborador é localizado por e-mail, CPF ou CNPJ.
          </>
        }
        templateHref="/modelo-comissoes.csv"
        onSuccess={aposMudanca}
      />

      <UploadPlanilhaModal
        open={uploadSaude}
        onClose={() => setUploadSaude(false)}
        endpoint={`/folhas/${id}/importar/amil-saude`}
        titulo="Importar Amil Saúde"
        descricao={
          <>
            Do extrato mensal entram três colunas. <strong>Dependente</strong> e{" "}
            <strong>Coparticipação Dependente</strong> são descontadas integralmente.{" "}
            <strong>Coparticipação Titular</strong> sofre a franquia de{" "}
            {formatCents(folha.franquiaAmil)}: só o que exceder esse valor é descontado.
            Mensalidade do titular, cobranças retroativas e qualquer coluna de procedimento não
            ficam gravadas.
          </>
        }
        templateHref="/modelo-amil-saude.csv"
        onSuccess={aposMudanca}
      />

      <UploadPlanilhaModal
        open={uploadOdonto}
        onClose={() => setUploadOdonto(false)}
        endpoint={`/folhas/${id}/importar/amil-odonto`}
        titulo="Importar Amil Odonto"
        descricao={
          <>
            Mesmo layout da saúde: <strong>Dependente</strong> e{" "}
            <strong>Coparticipação Dependente</strong> são descontadas integralmente. No
            odontológico a <strong>Coparticipação Titular é isenta</strong> e é ignorada.
          </>
        }
        templateHref="/modelo-amil-odonto.csv"
        onSuccess={aposMudanca}
      />

      <FecharFolhaDialog
        open={fecharOpen}
        onClose={() => setFecharOpen(false)}
        folhaId={id}
        competencia={folha.competencia}
        totais={totais}
        colaboradores={itens.length}
        bloqueios={detalhe.bloqueios}
        avisos={avisos}
        onFechada={aposMudanca}
        onCorrigir={corrigirBloqueio}
        onIgnorado={aposMudanca}
      />

      {ajustando && (
        <AjustarItemModal
          open={!!ajustando}
          onClose={() => setAjustando(null)}
          folhaId={id}
          item={ajustando}
          onSalvo={load}
        />
      )}

      {detalhando && (
        <Dialog open={!!detalhando} onOpenChange={(v) => !v && setDetalhando(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{detalhando.nome}</DialogTitle>
            </DialogHeader>
            <DemonstrativoCard
              dados={detalhando}
              competencia={folha.competencia}
              franquiaAmil={folha.franquiaAmil}
              titulo="Composição do valor"
            />
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={reabrirOpen} onOpenChange={setReabrirOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reabrir {formatMesRef(folha.competencia)}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  As baixas de empréstimo desta competência serão estornadas e o aviso de emissão
                  sai do ar até o novo fechamento.
                </p>

                {erp?.lancamentos.some((l) => l.contasPagarId) && (
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300">
                    Esta folha já foi lançada no ERP. Ao fechar de novo, o ERP não altera as contas a
                    pagar que já existem: quem mudar de valor fica como <strong>valor divergente</strong>{" "}
                    na aba ERP e precisa ser ajustado lá.
                  </div>
                )}
                {afetados.length > 0 && (
                  <div className="space-y-1 rounded-md border border-destructive/30 bg-destructive/5 p-3">
                    <p className="text-sm font-medium text-destructive">
                      {afetados.length} colaborador(es) já emitiram NF nesta competência:
                    </p>
                    <ul className="list-disc pl-5 text-xs">
                      {afetados.map((a) => (
                        <li key={a.colaboradorId}>
                          {a.nome} — NF {a.nfNumero} ({a.nfStatus})
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-muted-foreground">
                      Se o líquido mudar, a nota deles fica divergente do novo valor.
                    </p>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="motivo-reabertura">Motivo da reabertura</Label>
                  <Textarea
                    id="motivo-reabertura"
                    value={motivoReabertura}
                    onChange={(e) => setMotivoReabertura(e.target.value)}
                    rows={2}
                    maxLength={500}
                    placeholder="Ex: comissão do Bruno veio errada na planilha"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reabrindo}>Cancelar</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={motivoReabertura.trim().length < 5 || reabrindo}
              onClick={() => void reabrir()}
            >
              {reabrindo && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reabrir competência
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RoleGuard>
  );
};

export default RHFolhaDetalhe;
